// ssrc\pages\Admin\Views\Marketing\MarketingLandingPage\MarketingLandingPageForm.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import api from '../../../../../services/apiServices';
import { uploadFileToS3 } from '../../../../../utility/S3Uploader';
import { useToast } from '../../../../../hooks/use-toast';

// ⬇ Reuse the SAME CategorySearch you showed me
import { CategorySearch } from '../../../../../components/Admin/Category/CategorySearch';

import ImageUploader from '../../../../../components/ImageUploader';
import MediaLibraryModal from '../../../../../components/MediaLibraryModal';
import { Button } from '../../../../../components/ui/button';
import { Textarea } from '../../../../../components/ui/textarea';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { extractS3Path } from '../../../../../utility/stripS3Prefix';

const MemoizedImageUploader = React.memo(ImageUploader);

const META_TITLE_LIMIT = 60;
const META_DESC_LIMIT = 160;

const MarketingLandingPageForm = () => {
  // We assume route looks like /admin/marketing/landing-pages/:locale/:pageId?
  // If your current route is different, that's fine — we will gracefully handle undefined.
  const { pageId, locale } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isEditMode = Boolean(pageId);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    master_category_id: '',
    image_url: '',
    meta_title: '',
    meta_description: '',
    tags: '',
  });

  // purely for showing which category is chosen in the UI
  const [selectedCategoryName, setSelectedCategoryName] = useState('');

  const [imageFile, setImageFile] = useState(null);
  const [pageLoading, setPageLoading] = useState(false); // page fetch/save state
  const [isUploading, setIsUploading] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const [isMetaTitleManuallyEdited, setIsMetaTitleManuallyEdited] = useState(false);
  const [isMetaDescManuallyEdited, setIsMetaDescManuallyEdited] = useState(false);

  // 1. Load data in edit mode (if editing an existing landing page)
  useEffect(() => {
    if (!isEditMode) return;

    setPageLoading(true);
    api
      .get(`/admin/marketing/landing-pages/${pageId}`)
      .then(async (response) => {
        const page = response.data;
        setFormData(page);

        if (page.meta_title) {
          setIsMetaTitleManuallyEdited(true);
        }
        if (page.meta_description) {
          setIsMetaDescManuallyEdited(true);
        }

        // hydrate selectedCategoryName from backend
        if (page.master_category_id) {
          try {
            const catRes = await api.get(`/public/categories/by-id/${page.master_category_id}`, {
              params: { locale: 'master'},
            });
            const catName = catRes.data?.name || '';
            setSelectedCategoryName(catName);
          } catch (err) {
            console.error('Failed to fetch category name for prefill', err);
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch page data', error);
        navigate('/admin/marketing/buyer-landing-pages');
      })
      .finally(() => setPageLoading(false));
  }, [isEditMode, pageId, navigate]);

  // 2. Auto-populate meta_title from title unless manually overridden
  useEffect(() => {
    if (!isMetaTitleManuallyEdited && formData.title !== formData.meta_title) {
      setFormData((prev) => ({
        ...prev,
        meta_title: prev.title.substring(0, META_TITLE_LIMIT),
      }));
    }
  }, [formData.title, formData.meta_title, isMetaTitleManuallyEdited]);

  // 3. Auto-populate meta_description from description unless manually overridden
  useEffect(() => {
    const truncatedDesc = formData.description.substring(0, META_DESC_LIMIT);
    if (!isMetaDescManuallyEdited && truncatedDesc !== formData.meta_description) {
      setFormData((prev) => ({
        ...prev,
        meta_description: truncatedDesc,
      }));
    }
  }, [formData.description, formData.meta_description, isMetaDescManuallyEdited]);

  // ------- Handlers -------

  // Generic form field change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Explicit meta handlers so we mark them "manually edited"
  const handleMetaTitleChange = (e) => {
    setIsMetaTitleManuallyEdited(true);
    handleInputChange(e);
  };

  const handleMetaDescChange = (e) => {
    setIsMetaDescManuallyEdited(true);
    handleInputChange(e);
  };

  // 🔥 THIS is how we capture the category picked from CategorySearch
  // CategorySearch will pass us a "cat" object with { id, name, type, ... }
  const handleCategoryPicked = (cat) => {
    
    const resolvedId = cat.id; // CategorySearch always gives cat.id
    const resolvedName = cat.name || '';

    setFormData((prev) => ({
      ...prev,
      master_category_id: resolvedId,
    }));

    setSelectedCategoryName(resolvedName);
  };

  // Image upload prep
  const handleImageChange = useCallback((file) => {
    setImageFile(file);
  }, []);

  const initialUrls = useMemo(
    () => (formData.image_url ? [formData.image_url] : []),
    [formData.image_url]
  );

  const handleLibrarySelect = (url) => {
    setFormData((prev) => ({ ...prev, image_url: extractS3Path(url) }));
    setImageFile(null);
    setIsLibraryOpen(false);
  };

  // actual upload to S3 then store URL in formData.image_url
  const handleImageUpload = async () => {
    if (!imageFile) {
      toast({
        variant: 'destructive',
        title: 'No File',
        description: 'Please select an image file to upload.',
      });
      return;
    }

    setIsUploading(true);

    const timestamp = Date.now();
    const fileExt = imageFile.name.split('.').pop();
    const fileNameBase = imageFile.name.substring(0, imageFile.name.lastIndexOf('.'));
    const sanitizedBase = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
    const newFileName = `${sanitizedBase}-${timestamp}.${fileExt}`;

    const renamedFile = new File([imageFile], newFileName, { type: imageFile.type });

    try {
      
      const dynamic_url = `marketing/landing-pages`;
      const uploadedUrl = await uploadFileToS3(renamedFile, dynamic_url);

      setFormData((prev) => ({ ...prev, image_url: uploadedUrl }));

      toast({
        title: 'Upload Successful',
        description: 'Image URL has been updated.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Upload Failed',
        description: 'Could not upload the image.',
      });
    } finally {
        setIsUploading(false);
    }
  };

  // Submit create/update
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.master_category_id) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please select a category.',
      });
      return;
    }

    setPageLoading(true);

    try {
      if (isEditMode) {
        await api.put(`/admin/marketing/landing-pages/${pageId}`, formData);
        toast({
          title: 'Success',
          description: 'Landing page updated successfully.',
        });
      } else {
        await api.post('/admin/marketing/landing-pages', formData);
        toast({
          title: 'Success',
          description: 'Landing page created successfully.',
        });
      }

      navigate('/admin/marketing/buyer-landing-pages');
    } catch (error) {
      console.error('Failed to save page:', error);

      const errorMsg =
        error.response?.data?.detail || 'An unexpected error occurred.';

      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: errorMsg,
      });
    } finally {
      setPageLoading(false);
    }
  };

  // unified loading state (editing fetch OR submitting) can briefly show loader
  if ((pageLoading && isEditMode)) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
      <h2>{isEditMode ? 'Edit' : 'Create'} Marketing Landing Page</h2>

      {/* -------- CATEGORY PICKER (reusing your CategorySearch component) -------- */}
      <div className="mb-4">
        <Label>Category</Label>

        {selectedCategoryName && (
          <div
            style={{
              fontSize: '0.9rem',
              color: '#555',
              marginBottom: '4px',
            }}
          >
            Selected: {selectedCategoryName} (ID {formData.master_category_id || '—'})
          </div>
        )}

        <CategorySearch
          locale={'master'}      // fallback if locale isn't in route
          onSelect={handleCategoryPicked} // <-- critical: capture selection
        />
      </div>

      {/* --- Basic Content Fields --- */}
      <div>
        <Label htmlFor="title">Page Title</Label>
        <Input
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Enter the main title for the page"
        />
      </div>

      <div>
        <Label htmlFor="slug">Page Slug</Label>
        <Input
          id="slug"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          placeholder="my-awesome-landing-page"
        />
      </div>

      <div>
        <Label htmlFor="description">Page Description (Content)</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={10}
        />
      </div>

      {/* --- Images / Hero --- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <MemoizedImageUploader
          label="Upload New Page Image"
          onChange={handleImageChange}
          initialUrls={initialUrls}
          onBrowse={() => setIsLibraryOpen(true)}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <Label htmlFor="image_url">Image URL</Label>
            <Input
              id="image_url"
              name="image_url"
              value={formData.image_url}
              onChange={handleInputChange}
            />
          </div>

          <Button
            type="button"
            onClick={handleImageUpload}
            disabled={isUploading || !imageFile}
          >
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </Button>
        </div>
      </div>

      {/* --- Metadata / SEO --- */}
      <div
        style={{
          borderTop: '1px solid #e0e0e0',
          paddingTop: '24px',
        }}
      >
        <h3>Metadata for Ads & SEO</h3>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginTop: '16px',
          }}
        >
          <div>
            <Label htmlFor="meta_title">Meta Title</Label>
            <Input
              id="meta_title"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleMetaTitleChange}
            />
          </div>

          <div>
            <Label htmlFor="meta_description">Meta Description</Label>
            <Textarea
              id="meta_description"
              name="meta_description"
              value={formData.meta_description}
              onChange={handleMetaDescChange}
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags / Keywords</Label>
            <Input
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      {/* --- Actions --- */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}
      >
        <Button
          variant="outline"
          type="button"
          onClick={() => navigate('/admin/marketing/buyer-landing-pages')}
          disabled={pageLoading}
        >
          Cancel
        </Button>

        <Button type="submit" disabled={pageLoading || isUploading}>
          {pageLoading ? 'Saving...' : 'Save Page'}
        </Button>
      </div>
      </form>

      <MediaLibraryModal
        open={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        onSelect={handleLibrarySelect}
      />
    </>
  );
};

export default MarketingLandingPageForm;
